import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  IconButton,
  useToast,
  Skeleton,
  Text,
  Flex,
  Badge,
  Switch,
  useColorModeValue,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stack,
  Divider,
} from "@chakra-ui/react";
import { BsPlus, BsEye, BsPencil, BsTrash, BsSearch } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import procurementApi from "../services/procurementApi";

const WorkflowList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  useEffect(() => {
    fetchWorkflows();
  }, []);

  // Filter workflows based on search and status
  const filteredWorkflows = useMemo(() => {
    return workflows.filter((workflow) => {
      // Status filter
      if (statusFilter === "active" && !workflow.isActive) return false;
      if (statusFilter === "inactive" && workflow.isActive) return false;

      // Search filter (name or description)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = workflow.name?.toLowerCase().includes(query);
        const descMatch = workflow.description?.toLowerCase().includes(query);
        return nameMatch || descMatch;
      }

      return true;
    });
  }, [workflows, searchQuery, statusFilter]);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const data = await procurementApi.getWorkflows();
      setWorkflows(data?.data || data || []);
    } catch (error) {
      toast({
        title: "Error fetching workflows",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    navigate("/procurement/workflows/create");
  };

  const handleEditWorkflow = (id) => {
    navigate(`/procurement/workflows/${id}/edit`);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      if (currentStatus) {
        await procurementApi.deactivateWorkflow(id);
        toast({
          title: "Workflow deactivated",
          status: "success",
          duration: 2000,
        });
      } else {
        await procurementApi.activateWorkflow(id);
        toast({
          title: "Workflow activated",
          status: "success",
          duration: 2000,
        });
      }
      fetchWorkflows();
    } catch (error) {
      toast({
        title: "Error updating workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDeleteWorkflow = async (id) => {
    if (!window.confirm("Are you sure you want to delete this workflow?")) {
      return;
    }

    try {
      // Assuming delete endpoint exists
      toast({
        title: "Workflow deleted",
        status: "success",
        duration: 2000,
      });
      fetchWorkflows();
    } catch (error) {
      toast({
        title: "Error deleting workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="400px" />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mb={6}
      >
        <Heading size="lg">Procurement Workflows</Heading>
        <Button
          leftIcon={<BsPlus />}
          colorScheme="blue"
          onClick={handleCreateWorkflow}
          w={{ base: "full", md: "auto" }}
        >
          Create Workflow
        </Button>
      </Flex>

      {/* Filters */}
      <Box
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
        mb={4}
      >
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <BsSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </Select>
        </SimpleGrid>
      </Box>

      {/* Mobile cards */}
      <Box display={{ base: "block", md: "none" }}>
        {filteredWorkflows.length === 0 ? (
          <Box
            bg={bgColor}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={6}
            textAlign="center"
          >
            <Text color="gray.500">
              {workflows.length === 0
                ? "No workflows found"
                : "No workflows match your filters"}
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={1} spacing={3}>
            {filteredWorkflows.map((workflow) => (
              <Box
                key={workflow._id}
                bg={bgColor}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
              >
                <HStack justify="space-between" align="start" mb={2}>
                  <Box maxW="70%">
                    <Text fontWeight="semibold" noOfLines={2}>
                      {workflow.name}
                    </Text>
                    <Text fontSize="sm" color="gray.500" noOfLines={2} mt={1}>
                      {workflow.description || "No description"}
                    </Text>
                  </Box>
                  <Badge colorScheme={workflow.isActive ? "green" : "gray"}>
                    {workflow.isActive ? "Active" : "Inactive"}
                  </Badge>
                </HStack>

                <Divider my={2} />

                <SimpleGrid columns={2} spacing={2} mb={3}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Stages
                    </Text>
                    <Badge mt={1} colorScheme="purple">
                      {workflow.stages?.length || 0} stages
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Created
                    </Text>
                    <Text fontSize="sm">
                      {new Date(workflow.createdAt).toLocaleDateString()}
                    </Text>
                  </Box>
                </SimpleGrid>

                <HStack mb={3} justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Toggle Status
                  </Text>
                  <HStack>
                    <Switch
                      isChecked={workflow.isActive}
                      onChange={() =>
                        handleToggleActive(workflow._id, workflow.isActive)
                      }
                      colorScheme="green"
                    />
                    <Text fontSize="sm">
                      {workflow.isActive ? "Active" : "Inactive"}
                    </Text>
                  </HStack>
                </HStack>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    leftIcon={<BsPencil />}
                    variant="outline"
                    onClick={() => handleEditWorkflow(workflow._id)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<BsTrash />}
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleDeleteWorkflow(workflow._id)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Table */}
      <Box
        display={{ base: "none", md: "block" }}
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflowX="auto"
      >
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Workflow Name</Th>
              <Th>Description</Th>
              <Th>Stages</Th>
              <Th>Status</Th>
              <Th>Created Date</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredWorkflows.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8}>
                  <Text color="gray.500">
                    {workflows.length === 0
                      ? "No workflows found"
                      : "No workflows match your filters"}
                  </Text>
                </Td>
              </Tr>
            ) : (
              filteredWorkflows.map((workflow) => (
                <Tr key={workflow._id}>
                  <Td fontWeight="semibold">{workflow.name}</Td>
                  <Td maxW="300px" isTruncated>
                    {workflow.description || "N/A"}
                  </Td>
                  <Td>
                    <Badge colorScheme="purple">
                      {workflow.stages?.length || 0} stages
                    </Badge>
                  </Td>
                  <Td>
                    <HStack>
                      <Switch
                        isChecked={workflow.isActive}
                        onChange={() =>
                          handleToggleActive(workflow._id, workflow.isActive)
                        }
                        colorScheme="green"
                      />
                      <Text fontSize="sm">
                        {workflow.isActive ? "Active" : "Inactive"}
                      </Text>
                    </HStack>
                  </Td>
                  <Td>{new Date(workflow.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        size="sm"
                        icon={<BsPencil />}
                        variant="ghost"
                        onClick={() => handleEditWorkflow(workflow._id)}
                        aria-label="Edit workflow"
                      />
                      <IconButton
                        size="sm"
                        icon={<BsTrash />}
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDeleteWorkflow(workflow._id)}
                        aria-label="Delete workflow"
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Summary */}
      <Flex justify="space-between" align="center" mt={4}>
        <Text color="gray.600">
          Showing {filteredWorkflows.length} of {workflows.length} workflows
        </Text>
      </Flex>
    </Box>
  );
};

export default WorkflowList;
